import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Database, RefreshCw, Timer } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import SlowQueryDialog from "@/components/SlowQueryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sql-explorer/link-status`;
const POLL_MS = 30000;

const ago = (ts) => {
  if (!ts) return "—";
  const s = Math.max(0, Math.round(Date.now() / 1000 - ts));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return new Date(ts * 1000).toLocaleTimeString();
};

const tone = (st) => {
  if (!st) return { dot: "bg-zinc-400", label: "SQL …" };
  if (st.ok === false) return { dot: "bg-red-500", label: "SQL down" };
  if (st.ping_ms != null && st.ping_ms > 1500) return { dot: "bg-amber-500", label: "SQL slow" };
  return { dot: "bg-emerald-500", label: "SQL ok" };
};

// one shared poller for all badge instances (sidebar + mobile header)
const store = { st: null, busy: false, subs: new Set(), timer: null };
const emit = () => store.subs.forEach((fn) => fn({ st: store.st, busy: store.busy }));
const poll = async () => {
  if (store.busy) return;
  store.busy = true; emit();
  try {
    store.st = (await axios.get(API, { params: { ping: true } })).data;
  } catch {
    store.st = { ...(store.st || {}), ok: false, ping_ms: null };
  } finally {
    store.busy = false; emit();
  }
};
const subscribe = (fn) => {
  store.subs.add(fn);
  fn({ st: store.st, busy: store.busy });
  if (!store.timer) { poll(); store.timer = setInterval(poll, POLL_MS); }
  return () => {
    store.subs.delete(fn);
    if (store.subs.size === 0) { clearInterval(store.timer); store.timer = null; }
  };
};

export default function SqlLinkBadge({ compact = false }) {
  const [{ st, busy }, setState] = useState({ st: store.st, busy: store.busy });
  const [open, setOpen] = useState(false);
  const [slowOpen, setSlowOpen] = useState(false);
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; } })();

  const load = useCallback(() => poll(), []);

  useEffect(() => subscribe(setState), []);

  const t = tone(st);
  const rows = st ? [
    ["Round-trip", st.ping_ms != null ? `${st.ping_ms} ms${st.cold ? " (after fresh connect)" : ""}` : "—"],
    ["Last connect", st.last_connect_at ? `${ago(st.last_connect_at)} · ${st.last_connect_ms ?? "?"} ms` : "never"],
    ["Connections opened", st.connects ?? 0],
    ["Pool", `${st.idle ?? 0} idle · ${st.in_use ?? 0} busy`],
    ["Reused / new", `${st.hits ?? 0} / ${st.misses ?? 0}`],
    ["Last activity", ago(st.last_activity_at)],
    ["Tunnel", st.tunnel || "—"],
  ] : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="sql-link-badge"
          title={t.label}
          className={`inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white/80 hover:bg-zinc-50 transition-colors ${compact ? "p-1.5" : "px-2 py-1 text-[11px] font-mono text-zinc-600"}`}
        >
          <span className="relative flex h-2.5 w-2.5">
            {st?.ok !== false && <span className={`absolute inline-flex h-full w-full rounded-full ${t.dot} opacity-40 ${busy ? "animate-ping" : ""}`} />}
            <span data-testid="sql-link-dot" className={`relative inline-flex h-2.5 w-2.5 rounded-full ${t.dot}`} />
          </span>
          {!compact && <span data-testid="sql-link-label">{t.label}{st?.ping_ms != null ? ` ${st.ping_ms}ms` : ""}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid="sql-link-popover">
        <div className="px-3 py-2 border-b bg-zinc-50 flex items-center gap-2">
          <Database className="w-4 h-4 text-zinc-500" />
          <div className="flex-1">
            <p className="text-sm font-semibold">SQL link</p>
            <p className="text-[11px] text-zinc-500">ERP connection via {st?.tunnel || "…"}</p>
          </div>
          <span className={`h-2.5 w-2.5 rounded-full ${t.dot}`} />
        </div>
        <dl className="px-3 py-2 text-xs space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3">
              <dt className="text-zinc-500">{k}</dt>
              <dd className="font-mono text-zinc-800 text-right" data-testid={`sql-link-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
            </div>
          ))}
          {st?.last_error && (
            <div className="mt-1 rounded bg-red-50 border border-red-200 p-2 text-[11px] text-red-700 break-words" data-testid="sql-link-error">
              <span className="font-semibold">Last error</span> ({ago(st.last_error_at)}): {st.last_error}
            </div>
          )}
        </dl>
        <div className="px-3 pb-2 flex gap-2">
          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={load} disabled={busy} data-testid="sql-link-test-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Test now
          </Button>
          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => { setOpen(false); setSlowOpen(true); }} data-testid="sql-link-slow-btn">
            <Timer className="w-3 h-3 mr-1" /> Slow queries
          </Button>
        </div>
      </PopoverContent>
      <SlowQueryDialog open={slowOpen} onOpenChange={setSlowOpen} isAdmin={isAdmin} />
    </Popover>
  );
}
